import { getBooking } from "../../../app/api/getBooking";
import { getTravelAccounts } from "../../../app/api/getTravelAccounts";
import { BaseAction } from "../../../core/actions/BaseAction";
import { isNolAdmission, nolPassProduct } from "../../../Lib/Utils/nolCard";
import { IPassBooking } from "../../../Orchestrator/Types/Types/BookingTypes";

/**
 * Does this booking hold anything OTHER than a NOL (FMLM) pass?
 *
 * A Nol day pass answers to PRODUCT_CATEGORY.TRAVEL_PASS in the bookings
 * search, so a Nol-only booking arrives here looking like a pass booking. It is
 * not: a Nol pass is issued by Dubai's RTA and has no ER travel account.
 */
const holdsNonNolProduct = (booking: any): boolean =>
  (booking?.bookedOffers || []).some(
    (offer: any) =>
      !nolPassProduct(offer?.products) &&
      (offer?.admissions || []).some((admission: any) => !isNolAdmission(admission))
  );

/**
 * Fulfillment ids belonging to a NOL (FMLM) pass.
 *
 * A NOL pass is fulfilled under the SAME id as its admission, so this is the
 * only link back — `booking.fulfillments` entries carry no product, no
 * admission ref and (per docs/retailer.json) not even an issuer.
 */
const nolFulfillmentIds = (booking: any): Set<string> =>
  new Set(
    (booking?.bookedOffers || [])
      .flatMap((offer: any) => offer?.admissions || [])
      .filter((admission: any) => isNolAdmission(admission))
      .map((admission: any) => admission?.id)
      .filter(Boolean)
  );

export class GetPassBookingObjectAction extends BaseAction<IPassBooking | null, { bookingId: string }> {
  override async run(): Promise<IPassBooking | null> {
    let result: IPassBooking | null = null;
    const { bookingId } = this.params!;

    if (!bookingId) { return null; }

    try {
      const bookingDetailResponse = await getBooking(bookingId, false);
      if (bookingDetailResponse.status === "ok" && bookingDetailResponse.answer) {
        const bookingDetail = bookingDetailResponse.answer;

        // Nol and nothing else: leave before any lookup. Belt-and-braces now
        // that the endpoint itself is off (see getTravelAccounts).
        if (!holdsNonNolProduct(bookingDetail.booking)) {
          return null;
        }

        const nolIds = nolFulfillmentIds(bookingDetail.booking);
        const passengers = bookingDetail.booking?.passengers || [];
        let cardIssuer: string | undefined;
        let cardNumber: string | undefined;

        // `nolIds` also covers the card: a Nol pass card is numbered with the
        // pass's own id/control number, so a card matching one is the Nol pass
        // wearing a TRAVEL_PASS type rather than an ER pass.
        const nolControlNumbers = new Set(
          (bookingDetail.booking.fulfillments || [])
            .filter((fulfillment: any) => fulfillment?.id && nolIds.has(fulfillment.id))
            .map((fulfillment: any) => fulfillment?.controlNumber)
            .filter(Boolean)
        );

        for (const passenger of passengers) {
          const cards = (passenger as any).cards || [];
          for (const card of cards) {
            if (card?.type !== "TRAVEL_PASS" || !card.number) continue;
            if (nolIds.has(card.number) || nolControlNumbers.has(card.number)) continue;
            cardIssuer = card.issuer || cardIssuer;
            cardNumber = card.number;
            break;
          }
          if (cardNumber) break;
        }

        let issuer: string | undefined;
        let travelAccount: string | undefined;

        const passFulfillments = (bookingDetail.booking.fulfillments || []).filter(
          (fulfillment: any) => fulfillment?.id && !nolIds.has(fulfillment.id)
        );

        if (cardNumber) {
          issuer = cardIssuer || passFulfillments[0]?.issuer;
          travelAccount = cardNumber;
        } else {
          // Only NOL passes left (or none at all): nothing here owns a travel
          // account, so return without calling the endpoint.
          if (passFulfillments.length === 0) {
            return null;
          }

          const fulfillment = passFulfillments[0];
          if (!fulfillment.issuer || !fulfillment.controlNumber) {
            return null;
          }

          issuer = fulfillment.issuer;
          travelAccount = fulfillment.controlNumber;
        }

        if (!issuer || !travelAccount) {
          return null;
        }

        const travelAccountResponse = await getTravelAccounts({
          issuer: issuer,
          travelAccount: travelAccount
        }, false)

        // The payload must actually carry the account, not merely be a 200:
        // right after a pass is paid for, the account is not provisioned yet and
        // the response comes back without one. Returning a half-built object
        // here crashed the detail page (reading `balance` of undefined), so an
        // incomplete answer is treated as "no pass booking" instead.
        if (travelAccountResponse.status === "ok" && travelAccountResponse.answer?.travelAccount) {
          const travelAccountDetail = travelAccountResponse.answer;
          result = {
            booking: bookingDetail,
            travelAccount: travelAccountDetail
          }
        }
      }
    } catch (error) {
      console.error("Error fetching pass booking:", error);
    } finally {
      return result
    }
  }
}

export const getPassBookingObjectAction = new GetPassBookingObjectAction(); 
